import type { TKey, Translate } from '@/i18n'
import { statusLabel } from '@/features/sales/orders/model'
import type { Chat, ChatAttachment, ChatMessage } from '@/services/core/chat.service'

export const MAX_BODY = 4000
/** The server's chat file limit (client 2026-10-25: «Файл 10 мб»). */
export const MAX_FILE_SIZE = 10 * 1024 * 1024
export const MAX_FILES = 10
/** Own text messages are editable for an hour after sending. */
export const EDIT_WINDOW_MS = 60 * 60_000

export const canEdit = (message: ChatMessage, now = Date.now()) =>
  message.mine && message.kind === 'text' && !message.deleted_at && now - new Date(message.created_at).getTime() < EDIT_WINDOW_MS

export function chatTitle(chat: Pick<Chat, 'kind' | 'title' | 'order_number'>, t: Translate) {
  if (chat.kind === 'order') return t('chat.orderTitle', { number: chat.order_number || '—' })
  return chat.title || '—'
}

const ROLE_KEYS = new Set(['agent', 'moderator', 'branch_manager', 'admin', 'cashier', 'accountant', 'warehouse', 'driver', 'driver_supervisor', 'declarant'])

/** «employee.agent» → «Агент»; an unknown code shows nothing rather than the raw code. */
export function roleLabel(code: string, t: Translate) {
  const key = code.split('.').pop() ?? ''
  return ROLE_KEYS.has(key) ? t(`chat.roles.${key}` as TKey) : ''
}

export const isImage = (file: Pick<ChatAttachment, 'content_type' | 'name'>) =>
  file.content_type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|bmp)$/i.test(file.name)

export const isDeleted = (message: ChatMessage) => Boolean(message.deleted_at)

/** A system row: its text comes from i18n by system_code and payload; status names are the order status labels. */
export function systemText(message: ChatMessage, t: Translate) {
  const payload = message.system_payload ?? {}
  const driver = payload.driver_name || '—'
  switch (message.system_code) {
    case 'status_changed':
      return payload.from_status
        ? t('chat.system.status_changed', { from: statusLabel(payload.from_status, t), to: statusLabel(payload.status ?? '', t) })
        : t('chat.system.status_set', { to: statusLabel(payload.status ?? '', t) })
    case 'driver_assigned':
      return t('chat.system.driver_assigned', { driver })
    case 'driver_gave_back':
      return t('chat.system.driver_gave_back', { driver })
    case 'order_created':
      return t('chat.system.order_created', { number: payload.order_number ?? '—' })
    default:
      return message.body || t('chat.system.unknown')
  }
}

/** One line for the chat list: who wrote what. */
export function previewText(message: ChatMessage | null, t: Translate) {
  if (!message) return ''
  if (isDeleted(message)) return t('chat.deleted')
  if (message.kind === 'system') return systemText(message, t)
  let text = message.body.replace(/\s+/g, ' ').trim()
  if (!text && message.attachments.length) {
    text =
      message.attachments.length === 1
        ? isImage(message.attachments[0])
          ? t('chat.photo')
          : message.attachments[0].name || t('chat.file')
        : t('chat.files', { count: message.attachments.length })
  }
  const author = message.mine ? t('chat.you') : message.sender_name
  return author ? `${author}: ${text}` : text
}

const pad = (part: number) => String(part).padStart(2, '0')
const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate()

export function formatClock(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** The list's time column: «14:05» today, «24.10» this year, «24.10.2025» before. */
export function formatListTime(value: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  if (sameDay(date, now)) return formatClock(value)
  const day = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`
  return date.getFullYear() === now.getFullYear() ? day : `${day}.${date.getFullYear()}`
}

/** The day separator in a conversation. */
export function formatDayLabel(value: string, t: Translate) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  if (sameDay(date, now)) return t('chat.today')
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return t('chat.yesterday')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
}

export const dayKey = (value: string) => {
  const date = new Date(value)
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

export function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function newClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // Plain-http hosts have no randomUUID; getRandomValues is enough for an idempotency key.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
